// English overrides for the (Vietnamese-first) server language dictionary.
// When the user selects English in the avatar menu, LangProvider merges this
// map on top of the server dictionary, so /dashboard renders in English.
// Only UI copy is translated here — no role keys / logic values are changed.

export const EN_OVERRIDES = {
  // Locale menu label shown in the avatar dropdown
  DASHBOARD_LANGUAGE_MENU: 'Language',

  // Common
  COMMON_CLOSE: 'Close',
  COMMON_DELETE: 'Delete',
  COMMON_DESCRIPTION: 'Description',
  COMMON_EXCERPT: 'Excerpt',
  COMMON_TITLE: 'Title',
  COMMON_UPLOADING: 'Uploading...',

  // Tabs
  DASHBOARD_TAB_OVERVIEW: 'Overview',
  DASHBOARD_TAB_PROFILE: 'Profile',
  DASHBOARD_TAB_SECURITY: 'Security',
  DASHBOARD_TAB_MEMBERS: 'Member Management',
  DASHBOARD_TAB_ACHIEVEMENTS: 'Achievements (Home)',
  DASHBOARD_TAB_BLOGS: 'Blog Management (Home)',
  DASHBOARD_TAB_EVENTS_ADMIN: 'Event Management (Home)',
  DASHBOARD_TAB_CONTRIBUTORS: 'Contributor Management (Home)',
  DASHBOARD_TAB_ABOUT_GALLERY: 'Club Info Management (Home)',
  DASHBOARD_TAB_FORUM: 'Forum',
  DASHBOARD_TAB_RESOURCES: 'Resources',
  DASHBOARD_TAB_TRAINING: 'Training',
  DASHBOARD_TAB_SMTP: 'SMTP',
  DASHBOARD_TAB_FORUM_ADMIN: 'Forum',
  DASHBOARD_TAB_RESOURCES_ADMIN: 'Resources',
  DASHBOARD_TAB_MAIL_CAMPAIGN: 'Send Event Mail',
  DASHBOARD_TAB_EVENT_CALENDAR: 'Club Calendar',
  DASHBOARD_TAB_NOMINATION: 'Nomination',

  // Profile section
  DASHBOARD_AVATAR: 'Avatar',
  DASHBOARD_AVATAR_HINT: 'Upload a square image for best results.',
  DASHBOARD_CHANGE_AVATAR: 'Change Avatar',
  DASHBOARD_REMOVE_AVATAR: 'Remove Avatar',
  DASHBOARD_AVATAR_CONFIRM: 'Use this avatar?',
  DASHBOARD_AVATAR_SUCCESS: 'Avatar updated!',
  DASHBOARD_AVATAR_REMOVED: 'Avatar removed.',
  DASHBOARD_BIO: 'Bio',
  DASHBOARD_BIO_PLACEHOLDER: 'Tell us about yourself...',
  DASHBOARD_FULL_NAME: 'Full Name',
  DASHBOARD_PHONE: 'Phone',
  DASHBOARD_CLASS: 'Class',
  DASHBOARD_STUDENT_ID: 'Student ID',
  DASHBOARD_GEN: 'Gen',
  DASHBOARD_MAJOR: 'Major',
  DASHBOARD_MAJOR_PLACEHOLDER: 'Your major...',
  DASHBOARD_CTF_FIELD: 'CTF Field',
  DASHBOARD_CTF_FIELD_PRIMARY: 'Primary CTF Field',
  DASHBOARD_CTF_FIELD_SECONDARY: 'Secondary CTF Fields',
  DASHBOARD_CTF_REQUIRED: 'Please select a primary CTF field.',
  DASHBOARD_WEBSITES: 'Websites & Links',
  DASHBOARD_LINK_LABEL_PH: 'Label (e.g. GitHub)',
  DASHBOARD_LINK_URL_PH: 'https://...',
  DASHBOARD_PROFILE_THEME: 'Profile Theme',
  DASHBOARD_PROFILE_EFFECT: 'Profile Effect',
  DASHBOARD_PROFILE_CURSOR: 'Profile Cursor',
  DASHBOARD_SELECT_THEME: 'Select theme',
  DASHBOARD_SELECT_EFFECT: 'Select effect',
  DASHBOARD_SELECT_CURSOR: 'Select cursor',
  DASHBOARD_PROFILE_SUCCESS: 'Profile saved!',
  DASHBOARD_SAVE: 'Save',
  DASHBOARD_SAVE_PROFILE: 'Save Profile',

  // Photos / album
  DASHBOARD_PHOTOS: 'Profile Photos',
  DASHBOARD_PHOTOS_HINT: 'Upload up to 5 photos.',
  DASHBOARD_UPLOAD_PHOTOS: 'Upload Photos',
  DASHBOARD_PHOTOS_SUCCESS: 'Photos uploaded!',
  DASHBOARD_PHOTO_DELETE: 'Delete photo',
  DASHBOARD_PHOTO_DELETE_CONFIRM: 'Remove this photo from the album?',
  DASHBOARD_PHOTO_DELETED: 'Photo deleted.',

  // Music
  DASHBOARD_LOOP_MODE: 'Loop mode',
  DASHBOARD_MUSIC_AUTOPLAY_HINT: 'Autoplay when the profile opens.',
  DASHBOARD_MUSIC_DELETE_CONFIRM: 'Delete this track?',
  DASHBOARD_MUSIC_DELETE_ALL_CONFIRM: 'Delete ALL tracks?',
  DASHBOARD_MUSIC_RENAME_PROMPT: 'New track title:',
  DASHBOARD_MUSIC_DISPLAY_PH: 'Display title',

  // Security (password / email / username)
  DASHBOARD_CHANGE_PASSWORD: 'Change Password',
  DASHBOARD_CHANGE_PASSWORD_TITLE: 'Change Password',
  DASHBOARD_CURRENT_PASSWORD: 'Current Password',
  DASHBOARD_NEW_PASSWORD: 'New Password',
  DASHBOARD_NEW_PASSWORD_PLACEHOLDER: 'At least 6 characters',
  DASHBOARD_CONFIRM_PASSWORD: 'Confirm New Password',
  DASHBOARD_PASSWORD_MISMATCH: 'Passwords do not match.',
  DASHBOARD_PASSWORD_SUCCESS: 'Password changed!',
  DASHBOARD_PASSWORD_CONFIRM: 'Are you sure you want to change your password?',
  DASHBOARD_ACCOUNT_PASSWORD: 'Account Password',
  DASHBOARD_ACCOUNT_PASSWORD_HINT: 'Manage your login credentials.',
  DASHBOARD_CHANGE_EMAIL: 'Change Email',
  DASHBOARD_CURRENT_EMAIL: 'Current Email',
  DASHBOARD_NEW_EMAIL: 'New Email',
  DASHBOARD_NEW_EMAIL_PH: 'new@email.com',
  DASHBOARD_UPDATE_EMAIL: 'Update Email',
  DASHBOARD_EMAIL_SUCCESS: 'Email updated!',
  DASHBOARD_EMAIL_REQUIRED: 'Email and current password are required.',
  DASHBOARD_CHANGE_USERNAME: 'Change Username',
  DASHBOARD_CURRENT_USERNAME: 'Current Username',
  DASHBOARD_NEW_USERNAME: 'New Username',
  DASHBOARD_NEW_USERNAME_PH: 'new_username',
  DASHBOARD_UPDATE_USERNAME: 'Update Username',
  DASHBOARD_USERNAME_HINT: 'Letters, numbers and underscores only.',
  DASHBOARD_USERNAME_INVALID: 'Invalid username.',
  DASHBOARD_USERNAME_REQUIRED: 'Username is required.',
  DASHBOARD_USERNAME_SUCCESS: 'Username updated!',

  // Generic actions / labels
  DASHBOARD_ADD: 'Add',
  DASHBOARD_EDIT: 'Edit',
  DASHBOARD_DELETE: 'Delete',
  DASHBOARD_UPDATE: 'Update',
  DASHBOARD_CANCEL: 'Cancel',
  DASHBOARD_APPLY: 'Apply',
  DASHBOARD_KEEP: 'Keep',
  DASHBOARD_NONE: 'None',
  DASHBOARD_PUBLISH: 'Publish',
  DASHBOARD_PREVIEW: 'Preview',
  DASHBOARD_ACTIONS: 'Actions',
  DASHBOARD_SELECTED: 'Selected',
  DASHBOARD_STATUS: 'Status',
  DASHBOARD_STATUS_LABEL: 'Status',
  DASHBOARD_DATE: 'Date',
  DASHBOARD_TITLE: 'Title',
  DASHBOARD_DESCRIPTION: 'Description',
  DASHBOARD_CONTENT: 'Content',
  DASHBOARD_CONTENT_MD: 'Content (Markdown)',
  DASHBOARD_CONTENT_REQUIRED: 'Content is required.',
  DASHBOARD_ROLE: 'Role',
  DASHBOARD_POSITION: 'Member Level',
  DASHBOARD_EMAIL: 'Email',
  DASHBOARD_USERNAME: 'Username',
  DASHBOARD_CONFIRM_DELETE: 'Confirm delete?',
  DASHBOARD_DELETE_SELECTED: 'Delete selected',
  DASHBOARD_TAGS: 'Tags',
  DASHBOARD_TAGS_LABEL: 'Tags',
  DASHBOARD_TAGS_PH: 'tag1, tag2...',
  DASHBOARD_TAGS_PLACEHOLDER: 'tag1, tag2...',
  DASHBOARD_IMAGES: 'Images',
  DASHBOARD_ATTACH_IMAGES: 'Attach images',
  DASHBOARD_MARKDOWN_PLACEHOLDER: 'Write in Markdown...',

  // Members management
  DASHBOARD_ADD_MEMBER: 'Add Member',
  DASHBOARD_EDIT_MEMBER: 'Edit Member',
  DASHBOARD_TOTAL_MEMBERS: 'Total members',
  DASHBOARD_NO_MEMBERS: 'No members found.',
  DASHBOARD_JOIN_DATE: 'Join Date',
  DASHBOARD_JOIN_DATE_LABEL: 'Join Date',
  DASHBOARD_NEW_PASSWORD_LABEL: 'New Password',
  DASHBOARD_PW_LEAVE_BLANK: 'Leave blank to keep current',
  DASHBOARD_BULK_IMPORT: 'Bulk Import',
  DASHBOARD_BULK_UPLOAD: 'Bulk Upload',
  DASHBOARD_BULK_EDIT: 'Bulk Edit',
  DASHBOARD_BULK_FILE: 'File',
  DASHBOARD_MEMBER_BULK_DELETE_CONFIRM: 'Delete the selected members?',
  DASHBOARD_MEMBER_BULK_EDIT_CONFIRM: 'Apply changes to the selected members?',

  // Achievements
  DASHBOARD_ADD_ACHIEVEMENT: 'Add Achievement',
  DASHBOARD_EDIT_ACHIEVEMENT: 'Edit Achievement',
  DASHBOARD_ACHIEVEMENT_NAME: 'Title',
  DASHBOARD_ACHIEVEMENT_DATE: 'Date',
  DASHBOARD_ACHIEVEMENT_IMAGES: 'Images',
  DASHBOARD_ACHIEVEMENT_DELETE_CONFIRM: 'Delete this achievement?',
  DASHBOARD_NO_ACHIEVEMENTS: 'No achievements yet.',

  // Blogs / events
  DASHBOARD_NEW_BLOG: 'New Blog',
  DASHBOARD_EDIT_BLOG: 'Edit Blog',
  DASHBOARD_NO_BLOGS: 'No blogs yet.',
  DASHBOARD_NEW_EVENT: 'New Event',
  DASHBOARD_EVENT_DATE: 'Event Date',
  DASHBOARD_EVENT_LOCATION: 'Location',
  DASHBOARD_EVENT_COVER: 'Cover Image',
  DASHBOARD_EVENT_DELETE_CONFIRM: 'Delete this event?',

  // Resources
  DASHBOARD_ADD_RESOURCE: 'Add Resource',
  DASHBOARD_ADD_CATEGORY: 'Add Category',
  DASHBOARD_RESOURCE_DELETE_CONFIRM: 'Delete this resource?',
  DASHBOARD_RESOURCE_CAT_DELETE_CONFIRM: 'Delete this category and all its resources?',
  DASHBOARD_RESOURCE_EMPTY_ADMIN: 'No resources yet.',
  DASHBOARD_RESOURCE_EMPTY_USER: 'No resources in this section yet.',
  DASHBOARD_RESOURCE_FILE_LABEL: 'File',
  DASHBOARD_RESOURCE_URL_LABEL: 'URL',
  DASHBOARD_RESOURCE_PERM_LABEL: 'Permissions',
  DASHBOARD_RESOURCE_PERM_HINT: 'Restrict which member levels can see this.',

  // Forum
  FORUM_TITLE: 'Forum',
  FORUM_ALL: 'All',
  FORUM_ALL_TOPICS: 'All topics',
  FORUM_ANONYMOUS: 'Anonymous',
  FORUM_AUTHOR: 'Author',
  FORUM_EDIT_TOPIC: 'Edit topic',
  FORUM_FILTER_MODE: 'Filter mode',
  FORUM_FILTER_TOPIC: 'Filter topic',
  FORUM_MODE: 'Mode',
  FORUM_NEW_TOPIC: 'New topic',
  FORUM_NO_POSTS: 'No posts yet.',
  FORUM_NO_TOPICS: 'No topics yet.',
  FORUM_POST_LOGS: 'Post logs',
  FORUM_PUBLIC: 'Public',
  FORUM_TIME: 'Time',
  FORUM_TOPIC: 'Topic',
  FORUM_TOPIC_DESC: 'Topic description',
  FORUM_TOPIC_NAME: 'Topic name',
  FORUM_TOPICS: 'Topics',
  FORUM_VIEW: 'View',
  FORUM_LOGS_HINT: 'Deletion / moderation history.',

  // SMTP
  DASHBOARD_SMTP_TITLE: 'SMTP Settings',
  DASHBOARD_SMTP_HOST: 'Host',
  DASHBOARD_SMTP_PORT: 'Port',
  DASHBOARD_SMTP_USER: 'User',
  DASHBOARD_SMTP_PASSWORD: 'Password',
  DASHBOARD_SMTP_SECURE: 'Secure (TLS)',
  DASHBOARD_SMTP_FROM_NAME: 'From Name',
  DASHBOARD_SMTP_FROM_EMAIL: 'From Email',
  DASHBOARD_SMTP_SAVE: 'Save SMTP',
  DASHBOARD_SMTP_SAVED: 'SMTP settings saved.',
  DASHBOARD_SMTP_TEST: 'Send test',
  DASHBOARD_SMTP_TEST_TITLE: 'Send a test email',
  DASHBOARD_SMTP_TEST_TO: 'Recipient',
  DASHBOARD_SMTP_TESTING: 'Sending...',

  // Login
  LOGIN_FORM_PASSWORD: 'Password',

  // Profile edit — links / music / personal sections (previously hardcoded VI)
  DASHBOARD_ADD_LINK: '+ Add link',
  DASHBOARD_MUSIC_SECTION: 'Profile Background Music Playlist',
  DASHBOARD_MUSIC_UPLOAD_HINT: 'Upload music (max 200MB per file, MP3/WAV/OGG/M4A/FLAC).',
  DASHBOARD_MUSIC_RENAME: 'Rename',
  DASHBOARD_MUSIC_DELETE: 'Delete',
  DASHBOARD_MUSIC_CLEAR_ALL: 'Delete entire playlist',
  DASHBOARD_MUSIC_EMPTY: 'Playlist is empty.',
  DASHBOARD_LOOP_NONE: 'No loop',
  DASHBOARD_LOOP_ALL: 'Loop playlist',
  DASHBOARD_LOOP_ONE: 'Loop one track',
  DASHBOARD_MUSIC_QUEUE: 'Upload queue',
  DASHBOARD_MUSIC_ADD_QUEUE: '➕ Add to queue',
  DASHBOARD_MUSIC_CLEAR_QUEUE: 'Clear queue',
  DASHBOARD_STATUS_DONE: '✓ Done',
  DASHBOARD_STATUS_ERROR: 'Error',
  DASHBOARD_STATUS_WAITING: 'Waiting...',
  DASHBOARD_PERSONAL_ACHIEVEMENTS: 'Personal Achievements',
  DASHBOARD_ACHIEVEMENT_ADD_BTN: 'Add achievement',
  DASHBOARD_ACHIEVEMENT_UPDATE_BTN: 'Update achievement',
  DASHBOARD_PERSONAL_BLOG: 'Personal Blog',
  DASHBOARD_EDIT_BTN: 'Edit',
  DASHBOARD_DELETE_BTN: 'Delete',
  DASHBOARD_CANCEL_BTN: 'Cancel',
  DASHBOARD_LOADING_UPLOAD: '⏳ Uploading...',
};

export default EN_OVERRIDES;
